import { useCallback, useEffect, useRef, useState } from 'react';
import { HorizontalContainer } from '@picsart/design-system/components/HorizontalContainer';

import LinkButton from 'redesignedComponents/molecules/CTA/LinkButton';
import { isInViewport } from 'utils/isInViewport';
import useStyles from './styles';
import { AnimatedCardsProps } from './types';

enum ScrollDirection {
  UP = 'up',
  DOWN = 'down',
}

const AnimatedCards = ({ cards, acDuration, acButton, acBackground }: AnimatedCardsProps) => {
  const classes = useStyles({ acBackground });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection | null>(null);

  const lastScrollY = useRef(0);

  const handleScroll = useCallback(() => {
    const lastKnownScrollPosition = window.scrollY;
    const { offsetTop } = containerRef.current;

    if (lastKnownScrollPosition >= offsetTop && isInViewport(containerRef.current)) {
      if (lastKnownScrollPosition > lastScrollY.current) {
        setScrollDirection(ScrollDirection.DOWN);
      } else if (lastKnownScrollPosition < lastScrollY.current) {
        setScrollDirection(ScrollDirection.UP);
      }

      lastScrollY.current = lastKnownScrollPosition;
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return (
    <div
      ref={containerRef}
      className={classes.root}
      data-testid="animated-cards"
      data-pulse-section="animated-cards"
    >
      <HorizontalContainer className={classes.wrapper}>
        <div className={classes.container}>
          {cards.map((card, index) => {
            const topValue = ((card.acTop / 100) * window.innerHeight) / 2;
            const animateDirection = scrollDirection === ScrollDirection.UP ? 200 : -200;
            const translateY =
              card?.acAnimate && isInViewport(containerRef.current)
                ? animateDirection * (0.1 + index * 0.1) - topValue * 0.1
                : 0;

            return (
              // TODO: replace with Image, fix the inline styles
              <img
                key={`${card.acSrc}-${index}`}
                src={card.acSrc}
                alt={`image-${index}`}
                className={classes.image}
                style={{
                  top: `${card.acTop}%`,
                  right: `${card?.acRight}%`,
                  transform: `translate(${card?.acRight}%, ${translateY}%)`,
                  transitionDuration: `${acDuration}s`,
                  transitionProperty: 'transform',
                }}
                width={card.acWidth}
                height={card.acHeight}
              />
            );
          })}
          {acButton && (
            <div
              className={classes.button}
              data-testid="animated-cards-button"
              data-pulse-section="animated-cards-button"
            >
              <LinkButton {...acButton} />
            </div>
          )}
        </div>
      </HorizontalContainer>
    </div>
  );
};

export default AnimatedCards;
